import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-bbafcbb8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "action-block panel-outlined" }
const _hoisted_3 = { class: "flex justify-content-between sm:flex-row-reverse xl:flex-row gutter-x-s8" }
const _hoisted_4 = { class: "text-right caption" }
const _hoisted_5 = { class: "text-right h2 mt-s3" }
const _hoisted_6 = { class: "action-buttons gutter-x-s3" }
const _hoisted_7 = {
  key: 0,
  class: "w-6"
}
const _hoisted_8 = { class: "flex-grow-1" }

import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { Portfolio } from '@/sdk/entities/portfolio';
import { toDecimalPlacesOrLT } from '@/utils/format';
import { fromWei } from '@/sdk/utils';
import {
  farmStakedInUsd,
  portfolioLPToUsd,
} from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useStakedValue';
import { BIG_ZERO } from '@/utils/bigNumber';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import WalletConnect from '@/views/layout/wallet/WalletConnect.vue';
import LiquidityManagementDialog from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/LiquidityManagementDialog.vue';
import PortfolioUserShareChart from '@/views/pages/liquidity/portfolios/portfolio/portfolioCompound/PortfolioUserShareChart.vue';
import { useWallet } from '@/store/modules/wallet/useWallet';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useAllSteps } from '@/composables/useAllSteps';
import { usePortfoliosMilkomedaWSCBridge } from '@/store/modules/portfolios/usePortfoliosMilkomedaWSCBridge';
import { usePortfoliosMilkomedaWSCUnwrapBridge } from '@/store/modules/portfolios/usePortfoliosMilkomedaWSCUnwrapBridge';
import { checkIfNetworkIdDisabledToAdd } from '@/helpers/network-disable.helper';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'PortfolioActions',
  props: {
    portfolio: null
  },
  emits: ["sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  portfolio: Portfolio;
}




const { t } = useI18n();
const { walletState } = useWallet();
const { isInProgress } = useAllSteps();
const { isPresentTokenIntoNetwork, getTokenBySymbolAndChainId } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const { $reset: resetMilkomedaWSCBridge } = usePortfoliosMilkomedaWSCBridge();
const { $reset: resetMilkomedaWSCUnbridge } = usePortfoliosMilkomedaWSCUnwrapBridge();
const { state } = useStore();
const lpTokenInMilkomeda = computed(() => props.portfolio.lpToken);
const lpTokenInCardano = computed(() => {
  if (!isPresentTokenIntoNetwork(lpTokenInMilkomeda.value.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)) {
    return;
  }

  return getTokenBySymbolAndChainId(lpTokenInMilkomeda.value.symbol!, +DEFAULT_CARDANO_CHAIN_ID!);
});
// In TOKEN
const balanceLPInCardano = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) {
    return BIG_ZERO;
  }

  if (!lpTokenInCardano.value) {
    return BIG_ZERO;
  }

  const rawBalance = balanceByTokenSymbolAndChainId(
    lpTokenInCardano.value.symbol!,
    +DEFAULT_CARDANO_CHAIN_ID!,
  ).value?.balance;

  return BigNumber(rawBalance?.toExact() || BIG_ZERO);
});
const portfolioBalanceLPInWei = computed(() => props.portfolio.balanceOfWallet);
const farmStakedLPInWei = computed(
  () => state.farming.lp[props.portfolio.lpTokenAddress]?.deposited ?? BIG_ZERO,
);
// USD
const farmStakedUsd = computed(() => farmStakedInUsd(props.portfolio));
const depositUsd = computed(() => props.portfolio.getYouDepositUSD());
const balanceLPInCardanoUsd = computed(() =>
  portfolioLPToUsd(balanceLPInCardano.value, props.portfolio),
);
const totalDepositInUSDWithSymbol = computed(() => {
  loggingTotalDepositInUSD();

  return toDecimalPlacesOrLT(
    depositUsd.value.plus(farmStakedUsd.value).plus(balanceLPInCardanoUsd.value),
    {
      addUsdSymbol: true,
    },
  );
});
const isDeposit = computed(
  () => balanceLPInCardanoUsd.value.gt(0) || depositUsd.value.gt(0) || !farmStakedUsd.value.eq(0),
);
const isNetworkDisabledToAdd = computed(() => checkIfNetworkIdDisabledToAdd(DEFAULT_NETWORK_ID));
const isDisabledButton = computed<boolean>(() => {
  return ENABLE_FAKE_CARDANO_NETWORK && isInProgress.state;
});

const isShowLiquidityManagementDialog = ref(false);
const liquidityManagementDialogAction = ref('');

const showLiquidityManagementDialog = (action: string) => {
  resetMilkomedaWSCBridge();
  resetMilkomedaWSCUnbridge();
  liquidityManagementDialogAction.value = action;
  isShowLiquidityManagementDialog.value = true;
};
const closeLiquidityManagementDialog = () => {
  isShowLiquidityManagementDialog.value = false;
};
const userPortfolioSharePercent = computed(() => {
  if (!props.portfolio.totalSupply.gt(0)) {
    return new BigNumber(0);
  }

  loggingUserPortfolioShare();

  const depositWithFarmStaked = portfolioBalanceLPInWei.value.plus(farmStakedLPInWei.value);
  return depositWithFarmStaked.dividedBy(props.portfolio.totalSupply).multipliedBy(100);
});

const sendTransaction = () => {
  emits('sendTransaction');
};

// DEBUG

function isLoggingPortfoliosDisabled() {
  return !window['BLUESHIFT_DEBUG'].PORTFOLIOS;
}

function loggingTotalDepositInUSD() {
  if (isLoggingPortfoliosDisabled()) return;

  console.groupCollapsed(
    `[PORTFOLIO] [${props.portfolio.name} | ${props.portfolio.contractAddress}] Total Deposit In USD`,
  );
  console.log('deposit [ USD ] : ', depositUsd.value.toString());
  console.log('farm staked [ USD ] : ', farmStakedUsd.value.toString());
  console.log('balance LP in Cardano [ USD ] : ', balanceLPInCardanoUsd.value.toString());
  console.groupEnd();
}

function loggingUserPortfolioShare() {
  if (isLoggingPortfoliosDisabled()) return;

  console.groupCollapsed(
    `[PORTFOLIO] [${props.portfolio.name} | ${props.portfolio.contractAddress}] User portfolio share`,
  );
  console.log('portfolio balance LP [ LP ] : ', fromWei(portfolioBalanceLPInWei.value).toString());
  console.log('farm staked LP [ LP ] : ', fromWei(farmStakedLPInWei.value).toString());
  console.log('portfolio total supply [ LP ] : ', fromWei(props.portfolio.totalSupply).toString());
  console.groupEnd();
}

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(PortfolioUserShareChart, { userShare: _unref(userPortfolioSharePercent) }, null, 8, ["userShare"]),
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)('portfolio.myDeposit')), 1),
          _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(totalDepositInUSDWithSymbol)), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        (!_unref(walletState).isInjected)
          ? (_openBlock(), _createBlock(WalletConnect, {
              key: 0,
              "button-class": "w-full button-primary",
              class: "w-full"
            }))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (_unref(isDeposit))
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (_unref(walletState).isInjected)
                      ? (_openBlock(), _createBlock(_component_Button, {
                          key: 0,
                          class: "w-full button-base sm:px-s5",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (showLiquidityManagementDialog('withdraw'))),
                          disabled: _unref(isDisabledButton)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('withdraw')), 1)
                          ]),
                          _: 1
                        }, 8, ["disabled"]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_8, [
                (_unref(walletState).isInjected)
                  ? (_openBlock(), _createBlock(_component_Button, {
                      key: 0,
                      class: "w-full button-primary sm:px-s5",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (showLiquidityManagementDialog('addLiquidity'))),
                      disabled: _unref(isDisabledButton) || _unref(isNetworkDisabledToAdd)
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('addLiquidity')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
                  : _createCommentVNode("", true)
              ])
            ], 64))
      ])
    ]),
    (isShowLiquidityManagementDialog.value)
      ? (_openBlock(), _createBlock(LiquidityManagementDialog, {
          key: 0,
          portfolio: __props.portfolio,
          action: liquidityManagementDialogAction.value,
          onClose: closeLiquidityManagementDialog,
          onConfirm: sendTransaction
        }, null, 8, ["portfolio", "action"]))
      : _createCommentVNode("", true)
  ]))
}
}

})